import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { authHubAuth } from '../../../config/firebase.js';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { assertAuthApiOk } from '../../../utils/authApiErrors.js';
import { useToast } from '../../../contexts/ToastContext.jsx';
import HubAuthShell from './HubAuthShell.jsx';

const GENERIC_LOGIN_ERROR = 'クリニック名またはコード、ユーザーID、またはパスワードが正しくありません。';

const formatCandidateLabel = (candidate) => {
  const codePart = candidate.code ? ` (${candidate.code})` : '';
  const addressPart = candidate.address ? ` — ${candidate.address}` : '';
  return `${candidate.name}${codePart}${addressPart}`;
};

/**
 * ハブ用ログインページ
 * クリニック名またはコード + ユーザーID + パスワードでサーバー側ログイン
 */
const HubLoginPage = () => {
  const [clinicName, setClinicName] = useState('');
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [ambiguousCandidates, setAmbiguousCandidates] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const handleClinicInputChange = (value) => {
    setClinicName(value);
    setSelectedClinicId('');
    setAmbiguousCandidates([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        clinicName: clinicName.trim(),
        userId: userId.trim(),
        password,
      };
      if (selectedClinicId) {
        payload.clinicId = selectedClinicId;
      }

      const response = await fetch(buildApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 400 && data?.error === 'invalid-argument') {
        throw new Error(data.message || 'クリニックの選択が無効です。');
      }

      if (response.status === 409 && data?.error === 'clinic-ambiguous') {
        setAmbiguousCandidates(Array.isArray(data.candidates) ? data.candidates : []);
        setError(data.message || '同名のクリニックが複数見つかりました。選択してください。');
        return;
      }

      if (!response.ok || !data?.success) {
        if (response.status === 403 && data?.error === 'email-not-verified') {
          navigate('/verify-email/pending', {
            state: {
              clinicName: clinicName.trim(),
              clinicId: selectedClinicId || '',
              userId: userId.trim(),
              email: data.clinicEmail || '',
            },
          });
          return;
        }
        if (response.status === 401) {
          throw new Error('INVALID_LOGIN');
        }
        assertAuthApiOk(response, data, 'ログインに失敗しました。');
        throw new Error(data.message || data.error || 'ログインに失敗しました。');
      }

      if (data.authCustomToken) {
        await signInWithCustomToken(authHubAuth, data.authCustomToken);
      }

      showToast('auth.login.success');
      navigate('/dashboard');
    } catch (err) {
      console.error('ログインエラー:', err);

      if (err.message === 'INVALID_LOGIN') {
        setError(GENERIC_LOGIN_ERROR);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError(GENERIC_LOGIN_ERROR);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <HubAuthShell
      outside={(
        <div className="hub-auth-outside-links hub-panel-animated">
          <Link to="/signup" className="hub-auth-outside-link">
            新規アカウント登録はこちら
          </Link>
        </div>
      )}
    >
      <div className="hub-auth-header">
        <div className="hub-auth-logo">
          <img src="/logo.webp" alt="Synapse" style={{ width: 48, height: 48, objectFit: 'contain' }} />
        </div>
        <h1 className="hub-auth-title">ログイン</h1>
        <p className="hub-auth-subtitle">Synapseへようこそ</p>
      </div>

      {error && (
        <div className="hub-auth-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="hub-form-group">
          <label className="hub-form-label hub-form-required">クリニック名またはコード</label>
          <input
            type="text"
            className="hub-form-input"
            value={clinicName}
            onChange={(e) => handleClinicInputChange(e.target.value)}
            required
            autoComplete="organization"
          />
          <span className="hub-form-hint">
            クリニック名、または4文字のクリニックコードを入力してください
          </span>
        </div>

        {ambiguousCandidates.length > 0 && (
          <div className="hub-form-group">
            <label className="hub-form-label hub-form-required">クリニックを選択</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {ambiguousCandidates.map((candidate) => (
                <button
                  key={candidate.clinicId}
                  type="button"
                  className={`hub-btn ${selectedClinicId === candidate.clinicId ? 'hub-btn-primary' : 'hub-btn-secondary'}`}
                  style={{ width: '100%', textAlign: 'left' }}
                  onClick={() => setSelectedClinicId(candidate.clinicId)}
                >
                  {formatCandidateLabel(candidate)}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="hub-form-group">
          <label className="hub-form-label hub-form-required">ユーザーID</label>
          <input
            type="text"
            className="hub-form-input"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            required
            autoComplete="username"
          />
        </div>

        <div className="hub-form-group">
          <label className="hub-form-label hub-form-required">パスワード</label>
          <input
            type="password"
            className="hub-form-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </div>

        <button
          type="submit"
          className="hub-btn hub-btn-gradient hub-auth-login-btn"
          style={{ width: '100%' }}
          disabled={loading || (ambiguousCandidates.length > 0 && !selectedClinicId)}
        >
          {loading ? <LoadingSpinner size="small" /> : 'ログイン'}
        </button>
      </form>

      <div className="hub-auth-forgot">
        <Link to="/reset-password" className="hub-auth-forgot-link">
          パスワードをお忘れの方はこちら
        </Link>
        <Link to="/verify-email/pending" className="hub-auth-forgot-link" style={{ display: 'block', marginTop: 8 }}>
          確認メールが届かない方はこちら
        </Link>
      </div>
    </HubAuthShell>
  );
};

export default HubLoginPage;
